/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Backdrop from "@mui/material/Backdrop";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 2 PRO React components
import MDSnackbar from "components/MDSnackbar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDDatePicker from "components/MDDatePicker";
import MDTextarea from "components/MDTextarea";

// Material Dashboard 2 PRO React examples
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";

const initAttr = {
  id: "",
  code: "",
  remark: "",
  type_id: "",
  supplier_id: "",
  account_id: "",
  po_id: "0",
  po_supplier: "",
  po_total: "",
  po_vat: "",
  po_nettotal: "",
  po_paid: "",
  po_balance: "",
  po_type: "",
  po_user: "",
  po_code: "",
  po_remark: "",
  po_date: "",
  invoice_code: "",
  total: "",
  vat: "",
  nettotal: "",
  total_pay: "",
  vat_pay: "",
  nettotal_pay: "",
  vat_type: "NO",
  status: "edit",
  payment_type: "cash",
  slip_id: "",
  slip: "",
  print_url: "",
  created_at: "",
  updated_at: "",
  date_expire: Func.dateNow(),
  invoice_date: Func.dateNow(),
};

const cboPaymentType = [
  { id: "cash", name: "เงินสด" },
  { id: "transfer", name: "โอนเงิน" },
];

function CreditorPay({ parent }) {
  const mod = "purchase_pay";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [notify, setNotify] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon="notifications"
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.invoice_code, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalOpen(false);

    FetchData("PUT", `/${mod}`, app.token, {
      po_id: parent.id,
      remark: attr.remark,
      invoice_code: attr.invoice_code,
      invoice_date: attr.invoice_date,
      date_expire: attr.date_expire,
      total: attr.total,
      vat: attr.vat,
      nettotal: attr.nettotal,
      total_pay: attr.total,
      vat_pay: attr.vat,
      nettotal_pay: attr.nettotal,
      payment_type: attr.payment_type,
    }).then((result) => {
      setLoading(false);

      if (result.status === "ok") {
        showNotify(t(`alert.addcomplete`), "success");
      } else {
        setModalOpen(true);
        showNotify(t(`alert.saveerror`), "error");
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const renderInfo = () => {
    const readOnly = false;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label="เลขที่ใบแจ้งหนี้"
            onChange={(e) => setAttr({ ...attr, invoice_code: e.target.value })}
            value={attr.invoice_code}
            variant="standard"
            inputProps={{
              maxLength: 50,
              autoComplete: "off",
              readOnly: readOnly,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDDatePicker
            onChange={([date]) => {
              setAttr({ ...attr, invoice_date: Func.dateFormat(date) });
            }}
            value={new Date(attr.invoice_date)}
            input={{
              label: "วันที่ใบแจ้งหนี้",
              variant: "standard",
              fullWidth: true,
            }}
            readOnly={readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <MDDatePicker
            onChange={([date]) => {
              setAttr({ ...attr, date_expire: Func.dateFormat(date) });
            }}
            value={new Date(attr.date_expire)}
            input={{
              label: "วันกำหนดชำระ",
              variant: "standard",
              fullWidth: true,
            }}
            readOnly={readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label="วิธีการชำระเงิน"
            labelDefault={t(`element.please_select`)}
            data={cboPaymentType}
            value={attr.payment_type}
            onChange={(e) => setAttr({ ...attr, payment_type: e.target.value })}
            inputProps={{
              autoComplete: "off",
              readOnly: readOnly,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="จำนวนชำระ"
            onChange={(e) =>
              setAttr({
                ...attr,
                total: e.target.value,
                nettotal: e.target.value * 1 + attr.vat * 1,
              })
            }
            onClick={(e) => e.target.select()}
            value={attr.total}
            variant="standard"
            type="number"
            inputProps={{
              maxLength: 20,
              className: "text-right",
              autoComplete: "off",
              readOnly: readOnly,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="Vat"
            value={attr.vat}
            variant="standard"
            type="number"
            fullWidth
            onChange={(e) =>
              setAttr({
                ...attr,
                vat: e.target.value,
                nettotal: e.target.value * 1 + attr.total * 1,
              })
            }
            onClick={(e) => e.target.select()}
            inputProps={{
              maxLength: 20,
              className: "text-right",
              autoComplete: "off",
              readOnly: readOnly,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label="รวมชำระ"
            value={attr.nettotal}
            variant="standard"
            type="number"
            fullWidth
            inputProps={{
              disabled: true,
              className: "text-right",
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDTextarea
            label={`${t(`attr.remark`)} *`}
            onChange={(e) => setAttr({ ...attr, remark: e.target.value })}
            value={attr.remark}
            variant="standard"
            inputProps={{
              maxLength: 200,
              autoComplete: "off",
              readOnly: readOnly,
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth="xs"
        keepMounted
        aria-describedby="dialog-add"
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          {parent.code}
          <IconButton
            aria-label="close"
            onClick={() => setModalOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant="text" color="error" onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <MDButton variant="outlined" color="success" onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant="text" color="info" onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);
  }, []);

  return (
    <div>
      <MDButton
        variant="gradient"
        color="info"
        size="small"
        onClick={() => {
          setAttr(initAttr);
          setConfirm(false);
          setModalOpen(true);
        }}
      >
        <Icon>paid</Icon>&nbsp;ชำระเงิน
      </MDButton>
      {renderOpen()}
      {renderLoading()}
      {notify}
    </div>
  );
}

CreditorPay.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
  }).isRequired,
};

export default CreditorPay;
